import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FormControlLabel,
  FormControlLabelProps,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  RadioProps,
  Typography,
  useRadioGroup,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { X as XIcon } from 'react-feather';
import { BlueBankIcon, BlueCardIcon } from '~/modules/@shared/components';
import usePayrixIds from '~/modules/payment/hooks/usePayrixIds';

type Props = {
  handleModalNext: (paymentType: string) => void;
  handleModalClose: () => void;
  addPaymentMethodOpen: boolean;
  memberId?: number;
};

export default function ChoosePaymentMethodModal({
  memberId,
  handleModalNext,
  handleModalClose,
  addPaymentMethodOpen,
}: Props) {
  const { customerId } = usePayrixIds({ memberId });
  const [paymentType, setPaymentType] = useState('cc');

  useEffect(() => {
    if (!customerId) {
      setPaymentType('ach');
    }
  }, [customerId, setPaymentType]);

  if (!memberId) return null;

  return addPaymentMethodOpen ? (
    <Modal
      open={true}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box
        sx={{
          borderRadius: '10px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'rgb(246, 247, 251)',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
          <Typography variant="h2">Add a payment method</Typography>
          <IconButton onClick={handleModalClose}>
            <XIcon size={16} />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ px: 2 }}>
          <RadioGroup
            name="use-radio-group"
            defaultValue="cc"
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}>
            <MyFormControlLabel disabled={!customerId} value="cc" label="Credit/debit card" control={<MyRadio />} />

            <MyFormControlLabel
              value="ach"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body1">Bank Account</Typography>
                </Box>
              }
              control={<MyRadio />}
            />
          </RadioGroup>
        </Box>
        <Divider />
        <Box sx={{ pt: 3, pb: 3, pr: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Button variant="outlined" size="large" onClick={handleModalClose} sx={{ marginRight: '1em' }}>
            Cancel
          </Button>
          <Button variant="contained" size="large" onClick={() => handleModalNext(paymentType)} sx={{ width: '130px' }}>
            Next
          </Button>
        </Box>
      </Box>
    </Modal>
  ) : null;
}

type MyFormControlLabelProps = {
  value: string;
  disabled?: boolean;
};

function MyFormControlLabel(props: MyFormControlLabelProps & FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  const handleClick = () => {};

  return (
    <Card
      variant="outlined"
      sx={{ border: checked ? 'solid 2px #0AF' : 'inherit', padding: '2em', margin: '0.5em', cursor: 'pointer' }}
      onClick={handleClick}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <FormControlLabel sx={{ width: '100%', height: '100%' }} {...props} />

        {props.value === 'cc' ? (
          props.disabled ? (
            <Badge badgeContent=" " color="error" title="You need Payrix onboarding before adding a contact">
              <BlueCardIcon />
            </Badge>
          ) : (
            <BlueCardIcon />
          )
        ) : (
          <BlueBankIcon />
        )}
      </Box>
    </Card>
  );
}

function MyRadio(props: RadioProps) {
  return <Radio disableRipple color="default" sx={{ width: 'auto' }} {...props} />;
}
